import { HttpBaseApiWrapper } from './HttpBaseApiWrapper'

export interface Serializer<S, T> {
  fromJSON: (t: S) => T
  toJSON: (s: T) => S
}

/**
 * The DefaultSerializer doesn't do anything, just returns the same object in fromJSON and toJSON
 */
class DefaultSerializer<S, T> implements Serializer<S, T> {
  fromJSON(s: S) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    return s as any as T
  }
  toJSON(t: T) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    return t as any as S
  }
}

interface WithID {
  _id: string
}

class Cache<S extends WithID> {
  private map: Map<string, S>
  constructor() {
    this.map = new Map()
  }

  get(id: string) {
    return this.map.get(id)
  }

  delete(id: string) {
    return this.map.delete(id)
  }

  set(id: string, s: S) {
    return this.map.set(id, s)
  }

  setFromArray(s: S[]) {
    s.forEach((t) => this.set(t._id, t))
  }
}

/**
 * S ... JSON object
 * T ... Deserialized Object
 */
export class Resource<S, T extends WithID> {
  cache?: Cache<T>

  basePath: string
  http: HttpBaseApiWrapper
  serializer: Serializer<S, T>
  constructor(path: string, http: HttpBaseApiWrapper, serializer?: Serializer<S, T>, cached?: boolean) {
    this.basePath = `/${path}`
    this.http = http
    if (serializer) {
      this.serializer = serializer
    } else {
      this.serializer = new DefaultSerializer<S, T>()
    }

    if (cached) {
      this.cache = new Cache<T>()
    }
  }
  get(id): Promise<T> {
    if (this.cache) {
      // @ts-expect-error TS(2322) FIXME: Type 'T | undefined' is not assignable to type 'T'... Remove this comment to see the full error message
      const t: T = this.cache.get(id)
      if (t) {
        return Promise.resolve(t)
      }
    }
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
    return this.http.get<S>(`${this.basePath}/${id}`).then(
      (s) => {
        const t = this.serializer.fromJSON(s)
        if (this.cache) {
          this.cache.set(id, t)
        }
        return t
      },
      (err) => {
        if (this.cache) {
          this.cache.delete(id)
        }
        return Promise.reject(err)
      }
    )
  }
  getAll(ids?: string[]): Promise<T[]> {
    let promise: Promise<S[]>
    if (!ids) {
      promise = this.http.get<S[]>(this.basePath)
    } else {
      promise = this.http.get<S[]>(this.basePath, {
        params: {
          ids: ids.join(','),
        },
      })
    }

    return promise.then((array) => {
      return array.map((s) => {
        const t: T = this.serializer.fromJSON(s)
        if (this.cache) {
          this.cache.set(t._id, t)
        }
        return t
      })
    })
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  put(id: string, resource: any): Promise<T> {
    return this.http.put<T>(`${this.basePath}/${id}`, this.serializer.toJSON(resource) as object)
  }
  update(id: string, diff): Promise<T> {
    return this.http.put<S>(`${this.basePath}/${id}`, diff).then((s) => this.serializer.fromJSON(s))
  }
  delete(id): Promise<boolean> {
    if (this.cache) {
      this.cache.delete(id)
    }
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
    return this.http.delete(`${this.basePath}/${id}`)
  }
}
